import { useState, useMemo } from 'react';
import { adminService } from '../../../services/adminService';
import { useSnackbar } from '../../../components/common/Snackbar';
import { INITIAL_FORM_DATA, ROLE_DESIGNATIONS, REGIONS, getRegionConfig } from '../../../constants/usersPage';

/**
 * Hook to manage create user form state and validation
 * @param {Function} fetchUsers - Function to refetch users after creation
 * @param {Function} onClose - Function to close the dialog
 * @returns {Object} Form state and handlers
 */
export const useCreateUserForm = (fetchUsers, onClose) => {
  const { showSnackbar } = useSnackbar();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [whatsappError, setWhatsappError] = useState('');
  const [creating, setCreating] = useState(false);

  // Filter regions based on selected role using config
  const availableRegions = useMemo(() => {
    const roleConfig = getRegionConfig(formData.role);

    // If config says showOptions is false but it is visible/fixed as ALL, 
    // we technically don't need options, but if we want to show "ALL" in a dropdown (if designed that way)
    // The requirement says: "CH/MP ... Region 'ALL' ... Do not show a dropdown".
    // "ED/GM ... Show Region dropdown should not have 'ALL'".

    if (roleConfig.showOptions) {
      return REGIONS.filter(r => r.value !== 'ALL');
    }
    return REGIONS;
  }, [formData.role]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleWhatsAppChange = (event) => {
    const value = event.target.value;
    // Remove spaces, dashes, parentheses, dots
    let cleaned = value.replace(/[\s\-\(\)\.]/g, '');
    // Strip +91, 91, or leading 0 prefix before extracting digits
    if (cleaned.startsWith('+91')) cleaned = cleaned.substring(3);
    else if (cleaned.startsWith('91') && cleaned.replace(/\D/g, '').length > 10) cleaned = cleaned.substring(2);
    else if (cleaned.startsWith('0')) cleaned = cleaned.substring(1);
    // Only allow digits
    const numericValue = cleaned.replace(/[^0-9]/g, '');
    // Limit to 10 digits
    const limitedValue = numericValue.slice(0, 10);

    setFormData((prev) => ({ ...prev, whatsappNumber: limitedValue }));

    // Clear errors when user starts typing
    if (whatsappError) {
      setWhatsappError('');
    }
    if (errors.whatsappNumber) {
      setErrors((prev) => ({ ...prev, whatsappNumber: '' }));
    }
  };

  const handleWhatsAppKeyPress = (event) => {
    // Allow: backspace, delete, tab, escape, enter, and decimal point
    if (
      event.key === 'Delete' ||
      event.key === 'Backspace' ||
      event.key === 'Tab' ||
      event.key === 'Escape' ||
      event.key === 'Enter' ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress if not
    if (event.key < '0' || event.key > '9') {
      event.preventDefault();
    }
  };

  const handleRoleChange = (event) => {
    const selectedRole = event.target.value;
    const roleData = ROLE_DESIGNATIONS[selectedRole];

    // Clear error for role field
    if (errors.role) {
      setErrors((prev) => ({ ...prev, role: '' }));
    }

    setFormData((prev) => {
      const config = getRegionConfig(selectedRole);
      let newRegion = prev.region;

      if (config.isFixed && config.value) {
        newRegion = config.value;
      } else if (!config.showOptions && prev.region === 'ALL') {
        // If switching to a role where ALL implies fixed, handled above.
        // If switching to ED/GM (showOptions=true), we need to clear ALL.
        // Wait, logic:
        // If previous was ALL and new config allows ALL? No.
        // If new config requires manual selection (ED/GM), clear 'ALL'.
      }

      // Simplify:
      if (config.isFixed) {
        newRegion = config.value;
      } else if (config.showOptions) {
        // Must clear 'ALL' if present because ED/GM don't support it
        if (newRegion === 'ALL') newRegion = '';
      } else if (!config.isVisible) {
        newRegion = '';
      }

      return {
        ...prev,
        role: selectedRole,
        designation: roleData?.designation || '',
        escalationLevel: roleData?.escalationLevel || 1,
        region: newRegion,
        // Keep CH/MP projects managed by project hook auto-selection; reset when changing role.
        projects: [],
      };
    });
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleProjectsChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, projects: typeof value === 'string' ? value.split(',') : value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.role || formData.role.trim() === '') {
      newErrors.role = 'Role is required';
    } else {
      // Region validation based on role
      const roleConfig = getRegionConfig(formData.role);
      if (roleConfig.required && (!formData.region || formData.region === '')) {
        newErrors.region = 'Region is required';
      }
    }

    // Validate WhatsApp number (now mandatory)
    if (!formData.whatsappNumber || formData.whatsappNumber.trim() === '') {
      newErrors.whatsappNumber = 'WhatsApp number is required';
    } else if (formData.whatsappNumber.length !== 10) {
      newErrors.whatsappNumber = 'WhatsApp number must be exactly 10 digits';
      setWhatsappError('WhatsApp number must be exactly 10 digits');
    }

    return newErrors;
  };

  const handleCreateUser = async (projectOptions = []) => {
    // Validate all required fields
    const newErrors = validateForm();

    // If there are validation errors, set them and show toast
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showSnackbar('Please fill in all required fields correctly', 'error');
      return;
    }

    // Clear all errors if validation passes
    setErrors({});
    setWhatsappError('');

    setCreating(true);
    try {
      // Remove password from formData if empty (backend will generate it)
      const userData = { ...formData };
      if (!userData.password || userData.password.trim() === '') {
        delete userData.password;
      }

      // Persist project names in user profile while using unique values in UI selections.
      const isGlobalRole = userData.role === 'CH' || userData.role === 'MP';
      if (isGlobalRole) {
        userData.region = 'ALL';
        const allProjectValues = (projectOptions || []).map((project) => project.value).filter(Boolean);
        userData.projects = allProjectValues;
      }

      // Persist project names in user profile while using unique values in UI selections.
      if (Array.isArray(userData.projects) && userData.projects.length > 0) {
        const projectNameByValue = new Map(
          (projectOptions || []).map((project) => [project.value, project.name || project.label])
        );
        userData.projects = userData.projects
          .map((projectValue) => projectNameByValue.get(projectValue) || projectValue)
          .filter(Boolean);
      }

      // Add flag to request email notification with credentials
      userData.sendEmail = true;

      const response = await adminService.createUser(userData);

      // Show appropriate success message based on backend response
      let successMessage = 'User created successfully';
      if (response?.message) {
        successMessage = response.message;
      } else if (!userData.password) {
        successMessage = 'User created successfully. Password has been sent to their email.';
      } else {
        successMessage = 'User created successfully. Login credentials have been sent to their email.';
      }

      showSnackbar(successMessage, 'success');
      resetForm();
      onClose();
      fetchUsers(); // Refresh the user list
    } catch (error) {
      // console.error('Failed to create user:', error);
      const message = error.response?.data?.message || 'Failed to create user';
      showSnackbar(message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setWhatsappError('');
  };

  return {
    formData,
    errors,
    whatsappError,
    creating,
    availableRegions,
    handleInputChange,
    handleWhatsAppChange,
    handleWhatsAppKeyPress,
    handleRoleChange,
    handleSwitchChange,
    handleProjectsChange,
    handleCreateUser,
    resetForm,
    setFormData,
  };
};
